import React from "react";

export interface PrintDoctor {
  name?: string;
  tamil_name?: string;
  specializations?: string;
  tamil_specialization?: string;
  description?: string;
  registration_number?: string;
  registration_no?: string;
  tnmc_reg_no?: string;
  reg_no?: string;
}

interface ClinicLogoProps {
  className?: string;
  watermark?: boolean;
}

interface ClinicPrintHeaderProps {
  doctor?: PrintDoctor;
  title?: string;
  subtitle?: string;
  documentTitle?: string;
}

export const CLINIC_NAME_TAMIL = "பிரணவி கிளினிக்";
export const CLINIC_NAME_ENGLISH = "PRANAVE CLINIC";
export const VISITING_TIME_TAMIL = "பார்வை நேரம் : 09 : 00 AM to 01:00 PM & 07 : 00 PM to 09 : 00 PM";
export const CLINIC_ADDRESS_TAMIL = "2/2771, ஈ.பி ஆபீஸ் ரோடு, தொழில்பேட்டை, ரங்கசமுத்திரம், பொள்ளாச்சி வட்டம், கோயம்புத்தூர் மாவட்டம்";
export const CLINIC_ADDRESS = "2/2771, EB Office Road, Thozhilpettai, Rangasamuthiram, Pollachi TK, Coimbatore DT";
export const CLINIC_PHONE = "8637432525";

export const formatDoctorName = (name?: string | number) => {
  if (!name) return "-";
  const value = String(name);
  return value.toLowerCase().startsWith("dr.") ? value : `Dr. ${value}`;
};

export const doctorRegistrationNo = (doctor?: PrintDoctor) => {
  if (!doctor) return "-";
  return doctor.registration_number || doctor.tnmc_reg_no || doctor.registration_no || doctor.reg_no || "-";
};

export const doctorSpecialty = (doctor?: PrintDoctor) => {
  if (!doctor) return "-";
  return doctor.specializations || "-";
};

export const doctorTamilSpecialty = (doctor?: PrintDoctor) => doctor?.tamil_specialization || "";

export const ClinicLogo: React.FC<ClinicLogoProps> = ({ className = "h-20 w-20", watermark = false }) => (
  <img
    src="/pranave-logo.svg"
    alt="Pranave Clinic logo"
    className={className}
    style={{ objectFit: "contain", opacity: watermark ? 0.2 : 1 }}
  />
);

export const ClinicPrintFooter: React.FC<{ bordered?: boolean; includeInstructions?: boolean }> = ({
  bordered = false,
  includeInstructions = true,
}) => (
  <div className={`${bordered ? "border-t-2 border-[#2d2a78] pt-3" : ""} break-inside-avoid text-center text-sm font-semibold text-[#2d2a78] print:text-xs`}>
    {includeInstructions && (
      <div className="text-[11px] font-semibold leading-tight print:text-[10px]">
        <div>மறுமுறை வரும்போது இந்த சீட்டு கொண்டு வரவும்.</div>
        <div>மருந்துகள் ஏதேனும் பக்க விளைவுகளை ஏற்படுத்தினால் மருந்தை நிறுத்திவிட்டு உடனே மருத்துவரை தொடர்பு கொள்ளவும்.</div>
      </div>
    )}
    <div className={includeInstructions ? "mt-3" : ""}>{CLINIC_ADDRESS}</div>
    <div className="mt-1 text-orange-600">Ph : {CLINIC_PHONE}</div>
  </div>
);

const ClinicPrintHeader: React.FC<ClinicPrintHeaderProps> = ({ doctor, title = CLINIC_NAME_ENGLISH, subtitle, documentTitle }) => {
  const regNo = doctorRegistrationNo(doctor);
  const tamilDoctorName = doctor?.tamil_name || "-";
  const englishDoctorName = doctor?.name ? formatDoctorName(doctor.name) : "-";
  const tamilSpecialty = doctorTamilSpecialty(doctor) || "-";
  const englishSpecialtyText = doctorSpecialty(doctor);
  const clinicNameClass = "whitespace-nowrap text-[24px] font-black leading-[1.5]";
  const doctorNameClass = "min-w-0 break-words pt-1 text-[19px] font-bold leading-[1.5]";
  const detailClass = "min-w-0 break-words text-[14px] font-bold leading-[1.5]";

  return (
    <div className="clinic-print-header border-b-2 border-[#2d2a78] pb-3 text-[#2d2a78]">
      <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)] items-baseline gap-x-5 gap-y-1">
        <div className="row-span-4 flex flex-col items-center self-start">
          <ClinicLogo className="h-24 w-20" />
          <div className="mt-1 text-center text-[13px] font-black uppercase leading-tight">
            <div>Pranave</div>
            <div>Clinic</div>
          </div>
        </div>
        <div lang="ta" className={clinicNameClass}>{CLINIC_NAME_TAMIL}</div>
        <div lang="en" className={`${clinicNameClass} text-right uppercase`}>{title}</div>
        <div lang="ta" className={doctorNameClass}>{tamilDoctorName}</div>
        <div lang="en" className={`${doctorNameClass} text-right`}>{englishDoctorName}</div>
        <div className={detailClass}>TNMC Regd. No : {regNo}</div>
        <div className={`${detailClass} text-right`}>TNMC Regd. No : {regNo}</div>
        <div lang="ta" className={detailClass}>{tamilSpecialty}</div>
        <div lang="en" className={`${detailClass} text-right`}>{englishSpecialtyText}</div>
      </div>
      <div className="mt-4 text-center text-[14px] font-normal leading-tight">{subtitle || VISITING_TIME_TAMIL}</div>
      {documentTitle && <div className="mt-2 text-center text-lg font-black">{documentTitle}</div>}
    </div>
  );
};

export default ClinicPrintHeader;
